import React, { useContext, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { useTranslation } from "react-i18next";
import "./markdown.scss";

interface IMarkdownImageEditing {
  onImageResize?: (src: string, alt: string | undefined, width: number | null) => void;
  onImageRemove?: (src: string, alt: string | undefined) => void;
  onImageMove?: (src: string, alt: string | undefined, insertAt: number) => void;
  onImageDragHover?: (clientY: number | null) => number | null;
}

interface IMarkdownProps extends IMarkdownImageEditing {
  markdownText: string;
}

const MarkdownImageEditingContext = React.createContext<IMarkdownImageEditing>({});

const EXTERNAL_HREF_PATTERN = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;
const DOMAIN_HREF_PATTERN = /^(?:www\.)?[a-zA-Z\d](?:[a-zA-Z\d-]*[a-zA-Z\d])?(?:\.[a-zA-Z\d](?:[a-zA-Z\d-]*[a-zA-Z\d])?)+(?:[/?#].*)?$/;

export function normalizeMarkdownHref(href?: string): string | undefined {
  if (!href) {
    return href;
  }

  const trimmedHref = href.trim();
  if (!trimmedHref || trimmedHref.startsWith("#") || trimmedHref.startsWith("/")) {
    return trimmedHref;
  }

  if (EXTERNAL_HREF_PATTERN.test(trimmedHref)) {
    return trimmedHref;
  }

  if (DOMAIN_HREF_PATTERN.test(trimmedHref)) {
    return `https://${trimmedHref}`;
  }

  return trimmedHref;
}

function MarkdownLink({
  href,
  children,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
  delete props.node;
  const normalizedHref = normalizeMarkdownHref(href);

  if (!normalizedHref) {
    return <span>{children}</span>;
  }

  return (
    <a {...props} href={normalizedHref} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function MarkdownImage({
  src,
  alt,
  "data-sourcepos": sourcePos,
  ...rest
}: React.ImgHTMLAttributes<HTMLImageElement> & { node?: unknown; "data-sourcepos"?: string }) {
  delete rest.node;
  const { onImageResize, onImageRemove, onImageMove } = useContext(MarkdownImageEditingContext);

  if ((!onImageResize && !onImageRemove && !onImageMove) || !src) {
    return <img src={src} alt={alt} {...rest} />;
  }

  return (
    <EditableMarkdownImage
      src={src}
      alt={alt}
      sourcePos={sourcePos}
      onImageResize={onImageResize}
      onImageRemove={onImageRemove}
      onImageMove={onImageMove}
      {...rest}
    />
  );
}

function EditableMarkdownImage({
  src,
  alt,
  sourcePos,
  onImageResize,
  onImageRemove,
  onImageMove,
  style,
  ...rest
}: React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  sourcePos?: string;
  onImageResize?: (src: string, alt: string | undefined, width: number | null) => void;
  onImageRemove?: (src: string, alt: string | undefined) => void;
  onImageMove?: (src: string, alt: string | undefined, insertAt: number) => void;
}) {
  const { t } = useTranslation();
  const { onImageDragHover } = useContext(MarkdownImageEditingContext);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const imageStyle = dragWidth ? { ...style, width: dragWidth, height: "auto" } : style;

  function handlePointerDown(event: React.PointerEvent<HTMLSpanElement>) {
    event.preventDefault();
    event.stopPropagation();

    const handle = event.currentTarget;
    const image = handle.parentElement?.querySelector("img");
    if (!image) {
      return;
    }

    const startX = event.clientX;
    const startWidth = image.getBoundingClientRect().width;
    const maxWidth = image.closest(".markdown-display")?.clientWidth || startWidth;
    handle.setPointerCapture(event.pointerId);

    const widthAt = (clientX: number) => {
      const next = Math.round(startWidth + clientX - startX);
      return Math.min(Math.max(next, 80), maxWidth);
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      setDragWidth(widthAt(moveEvent.clientX));
    };

    const handlePointerEnd = (endEvent: PointerEvent) => {
      handle.removeEventListener("pointermove", handlePointerMove);
      handle.removeEventListener("pointerup", handlePointerEnd);
      handle.removeEventListener("pointercancel", handlePointerEnd);
      if (endEvent.type === "pointercancel") {
        setDragWidth(null);
        return;
      }

      const nextWidth = widthAt(endEvent.clientX);
      setDragWidth(null);
      onImageResize?.(src, typeof alt === "string" ? alt : undefined, nextWidth >= maxWidth - 8 ? null : nextWidth);
    };

    handle.addEventListener("pointermove", handlePointerMove);
    handle.addEventListener("pointerup", handlePointerEnd);
    handle.addEventListener("pointercancel", handlePointerEnd);
  }

  function handleRemove(event: React.MouseEvent<HTMLSpanElement>) {
    event.preventDefault();
    event.stopPropagation();
    onImageRemove?.(src, typeof alt === "string" ? alt : undefined);
  }

  function handleMovePointerDown(event: React.PointerEvent<HTMLSpanElement>) {
    if (!onImageMove) {
      return;
    }

    const target = event.target as HTMLElement;
    if (target.closest(".markdown-image-resize, .markdown-image-remove")) {
      return;
    }

    event.preventDefault();
    const frame = event.currentTarget;
    const startX = event.clientX;
    const startY = event.clientY;
    let dragging = false;
    frame.setPointerCapture(event.pointerId);

    const handlePointerMove = (moveEvent: PointerEvent) => {
      if (!dragging && Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) < 5) {
        return;
      }

      dragging = true;
      frame.classList.add("markdown-image-frame-moving");
      onImageDragHover?.(moveEvent.clientY);
    };

    const handlePointerEnd = (endEvent: PointerEvent) => {
      frame.removeEventListener("pointermove", handlePointerMove);
      frame.removeEventListener("pointerup", handlePointerEnd);
      frame.removeEventListener("pointercancel", handlePointerEnd);
      frame.classList.remove("markdown-image-frame-moving");
      const insertAt = dragging && endEvent.type !== "pointercancel" ? onImageDragHover?.(endEvent.clientY) : null;
      onImageDragHover?.(null);

      if (insertAt != null) {
        onImageMove(src, typeof alt === "string" ? alt : undefined, insertAt);
      }
    };

    frame.addEventListener("pointermove", handlePointerMove);
    frame.addEventListener("pointerup", handlePointerEnd);
    frame.addEventListener("pointercancel", handlePointerEnd);
  }

  return (
    <span
      className={onImageMove ? "markdown-image-frame markdown-image-frame-movable" : "markdown-image-frame"}
      data-sourcepos={sourcePos}
      title={onImageMove ? t("Drag to move") : undefined}
      onPointerDown={handleMovePointerDown}
    >
      <img {...rest} src={src} alt={alt} draggable={false} style={imageStyle} />
      {dragWidth ? <span className="markdown-image-size">{dragWidth}px</span> : null}
      {onImageRemove ? (
        <span
          className="markdown-image-remove"
          role="button"
          aria-label={t("Remove image")}
          title={t("Remove image")}
          onClick={handleRemove}
          onPointerDown={event => event.stopPropagation()}
        />
      ) : null}
      {onImageResize ? (
        <span
          className="markdown-image-resize"
          role="button"
          aria-label={t("Resize image")}
          title={t("Drag to resize")}
          onPointerDown={handlePointerDown}
        />
      ) : null}
    </span>
  );
}

function sourceOffsetAt(markdown: string, line: number, column: number) {
  const lines = markdown.split("\n");
  let offset = 0;

  for (let index = 0; index < line - 1 && index < lines.length; index++) {
    offset += lines[index].length + 1;
  }

  const lineText = lines[line - 1] ?? "";
  return offset + Math.min(Math.max(column - 1, 0), lineText.length);
}

function readSourceStart(sourcePos: string | undefined, markdown: string) {
  const match = sourcePos?.match(/^(\d+):(\d+)-\d+:\d+$/);
  if (!match) {
    return null;
  }

  return sourceOffsetAt(markdown, Number(match[1]), Number(match[2]));
}

export function formatMarkdownBreaks(markdownText: string) {
  if (markdownText === undefined || markdownText === null) {
    return "";
  } else {
    const markdownTextLines = markdownText.split("\n");
    const formattedMarkdownTextLines = markdownTextLines.map(line => line + "  ").join("\n");

    return formattedMarkdownTextLines;
  }
}

export default function Markdown(props: IMarkdownProps) {
  const editableRef = useRef<HTMLDivElement>(null);
  const [dropTop, setDropTop] = useState<number | null>(null);
  const customSanitize = {
    ...defaultSchema,
    attributes: {
      ...defaultSchema.attributes,
      a: [...(defaultSchema.attributes.a || []), "target", "rel"],
      img: [...(defaultSchema.attributes.img || []), "style", "width", "height"],
    },
  };

  // This to only allow width and height styles in img tags
  const sanitizeStylePlugin = () => {
    return tree => {
      // Start walking the tree from the root.
      walk(tree);
    };
  };

  // A recursive function to traverse the tree.
  function walk(node) {
    // 1. Process the current node.
    // Check if it's an element with a style property.
    if (node.type === "element" && node.properties && typeof node.properties.style === "string") {
      const styleString = node.properties.style as string;
      const allowedProperties = ["width", "height"];

      const sanitizedStyles = styleString
        .split(";")
        .map(declaration => declaration.trim())
        .filter(Boolean)
        .filter(declaration => {
          const propertyName = declaration.split(":")[0].trim().toLowerCase();
          return allowedProperties.includes(propertyName);
        })
        .join("; ");

      if (sanitizedStyles) {
        node.properties.style = sanitizedStyles;
      } else {
        delete node.properties.style;
      }
    }

    // 2. If the node has children, recurse into them.
    if (node.children && Array.isArray(node.children)) {
      for (const child of node.children) {
        walk(child); // This is the recursive call
      }
    }
  }

  function imageDragHover(clientY: number | null) {
    if (clientY == null || !editableRef.current) {
      setDropTop(null);
      return null;
    }

    const display = editableRef.current.querySelector(".markdown-display");
    if (!display) {
      setDropTop(null);
      return null;
    }

    const markdown = props.markdownText ?? "";
    const blocks = [...display.children].filter(
      (child): child is HTMLElement => child instanceof HTMLElement && Boolean(child.dataset.sourcepos),
    );
    const hostRect = editableRef.current.getBoundingClientRect();
    const slots: Array<{ offset: number; top: number }> = [];

    blocks.forEach((block, index) => {
      const start = readSourceStart(block.dataset.sourcepos, markdown);
      if (start == null) {
        return;
      }

      const box = block.getBoundingClientRect();
      if (index === 0) {
        slots.push({ offset: start, top: box.top - hostRect.top });
      }

      const next = blocks[index + 1];
      if (!next) {
        slots.push({ offset: markdown.length, top: box.bottom - hostRect.top });
        return;
      }

      const nextStart = readSourceStart(next.dataset.sourcepos, markdown);
      if (nextStart == null) {
        return;
      }

      const nextBox = next.getBoundingClientRect();
      slots.push({
        offset: nextStart,
        top: (box.bottom + nextBox.top) / 2 - hostRect.top,
      });
    });

    if (slots.length === 0) {
      slots.push({ offset: 0, top: 0 });
    }

    const nearest = slots.reduce((best, slot) => {
      const distance = Math.abs(clientY - (hostRect.top + slot.top));
      const bestDistance = Math.abs(clientY - (hostRect.top + best.top));
      return distance < bestDistance ? slot : best;
    });

    setDropTop(nearest.top);
    return nearest.offset;
  }

  const imageEditing = Boolean(props.onImageResize || props.onImageRemove || props.onImageMove);
  const markdown = (
    <ReactMarkdown
      className="markdown-display h-[revert]"
      sourcePos={Boolean(props.onImageMove)}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, customSanitize], sanitizeStylePlugin]}
      components={{
        a: MarkdownLink,
        ...(imageEditing ? { img: MarkdownImage } : {}),
      }}
    >
      {formatMarkdownBreaks(props.markdownText)}
    </ReactMarkdown>
  );

  if (!imageEditing) {
    return markdown;
  }

  return (
    <MarkdownImageEditingContext.Provider
      value={{
        onImageResize: props.onImageResize,
        onImageRemove: props.onImageRemove,
        onImageMove: props.onImageMove,
        onImageDragHover: props.onImageMove ? imageDragHover : undefined,
      }}
    >
      <div className="markdown-editable" ref={editableRef}>
        {markdown}
        {dropTop != null ? <div className="markdown-image-drop-line" style={{ top: dropTop }} /> : null}
      </div>
    </MarkdownImageEditingContext.Provider>
  );
}
